import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { TEAL } from "./constants";
import { IconSparkle } from "../../lib/Icons/WhiteboardIcons";

const GAP = 10;
const EDGE = 8;

/** Keeps the bar clear of the selection: above it when it fits, below it otherwise. */
function place({ anchor, bar, view }) {
  const top = anchor.y;
  const bottom = Number.isFinite(anchor.bottom) ? anchor.bottom : top;
  const height = bar.h || 44;
  const above = top - GAP - height;
  const below = bottom + GAP;
  const fitsAbove = above >= EDGE;
  const fitsBelow = !view.h || below + height <= view.h - EDGE;

  let x = anchor.x;
  const half = (bar.w || 0) / 2;
  if (view.w && bar.w + EDGE * 2 <= view.w) {
    x = Math.min(Math.max(x, EDGE + half), view.w - EDGE - half);
  } else if (view.w) {
    x = view.w / 2;
  }

  return {
    left: x,
    top: fitsAbove ? above : fitsBelow ? below : Math.max(EDGE, above),
  };
}

function Spinner({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" className="animate-spin flex-shrink-0" aria-hidden="true">
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M10 2a8 8 0 0 1 8 8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export default function AdjustBar({ screenPos, busy, error, result, onSubmit, onDismiss, onKeep, onRetry }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const boxRef = useRef(null);
  const [bar, setBar] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (busy) setOpen(true);
  }, [busy]);

  const choosing = Boolean(result) && !busy;

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setBar((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, [open, busy, error, choosing]);

  const close = () => {
    setOpen(false);
    setText("");
    onDismiss?.();
    onKeep?.();
  };

  if (!screenPos) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    if (choosing) onRetry?.(text.trim());
    else onSubmit(text.trim());
  };

  const parent = boxRef.current?.offsetParent;
  const view = { w: parent?.clientWidth || 0, h: parent?.clientHeight || 0 };
  const style = { ...place({ anchor: screenPos, bar, view }), transform: "translateX(-50%)" };

  if (!open && !busy && !error) {
    return (
      <button
        ref={boxRef}
        type="button"
        onClick={() => setOpen(true)}
        onMouseDown={(e) => e.stopPropagation()}
        className="absolute z-[55] flex items-center gap-[6px] px-[10px] py-[6px] rounded-full border cursor-pointer font-['DM_Sans'] text-[11px] font-semibold shadow-[0_4px_14px_rgba(0,0,0,0.14)]"
        style={{ ...style, background: "#FFFFFF", borderColor: "rgba(77,184,181,0.45)", color: TEAL }}
      >
        <IconSparkle size={14} />
        Ask Lumen
      </button>
    );
  }

  return (
    <div
      ref={boxRef}
      className="absolute z-[55] rounded-xl border border-black/[0.08] shadow-[0_10px_30px_rgba(0,0,0,0.18)] font-['DM_Sans']"
      style={{ ...style, background: "#FFFFFF", width: 360 }}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <form onSubmit={submit} className="flex items-center gap-2 px-3 py-[9px]">
        <span style={{ color: TEAL }}>
          {busy ? <Spinner size={16} /> : <IconSparkle size={16} />}
        </span>
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
          placeholder="Clean this up, or turn it into a chart…"
          className="flex-1 text-[12.5px] bg-transparent border-none outline-none text-slate-700 placeholder:text-slate-400 disabled:text-slate-400"
        />
        {choosing ? (
          <>
            <button type="submit" disabled={!text.trim()}
              className="text-[11px] font-semibold px-3 py-[5px] rounded-md cursor-pointer border bg-white disabled:opacity-45"
              style={{ color: TEAL, borderColor: "rgba(77,184,181,0.45)" }}>
              Try again
            </button>
            <button type="button" onClick={close}
              className="text-[11px] font-semibold px-3 py-[5px] rounded-md cursor-pointer border-none text-white"
              style={{ background: TEAL }}>
              OK
            </button>
          </>
        ) : (
          <button type="submit" disabled={busy || !text.trim()}
            className="flex items-center gap-[5px] text-[11px] font-semibold px-3 py-[5px] rounded-md cursor-pointer border-none text-white disabled:opacity-45"
            style={{ background: TEAL }}>
            {busy && <Spinner />}
            {busy ? "Working…" : "Generate"}
          </button>
        )}
        <button type="button" title="Close prompt" disabled={busy}
          onClick={close}
          className="text-slate-400 text-[15px] leading-none bg-transparent border-none cursor-pointer px-1 disabled:opacity-30 disabled:cursor-default">
          ✕
        </button>
      </form>

      {busy && (
        <>
          <div className="h-[3px] overflow-hidden rounded-b-xl" style={{ background: "rgba(77,184,181,0.15)" }}>
            <div className="h-full w-1/3 rounded-full animate-[adjustSlide_1.1s_ease-in-out_infinite]"
              style={{ background: TEAL }} />
          </div>
          <style>{"@keyframes adjustSlide{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}"}</style>
          <p className="text-[11px] text-slate-400 px-3 py-[7px] m-0 leading-snug">
            Lumen is looking at your selection. This usually takes a few seconds.
          </p>
        </>
      )}

      {choosing && (
        <p className="text-[11px] text-slate-400 px-3 pb-[9px] m-0 leading-snug">
          Happy with it? Press OK. Try again goes back to what you selected and makes a new version, with any changes to the words.
        </p>
      )}

      {error && <p className="text-[11px] text-red-500 px-3 pb-[9px] m-0 leading-snug">{error}</p>}
    </div>
  );
}
